<script lang="ts">
  // Organization management. Two audiences share one dialog because they overlap almost entirely:
  //
  //  • an organization admin manages their own member list;
  //  • the platform owner (super-admin) additionally creates organizations and appoints their first
  //    administrator, and can manage the members of any of them.
  //
  // Every action is authorized on the server against the organization in the path — this UI only
  // decides which controls are worth showing. A member who is not an admin still sees the roster,
  // because knowing who is in your firm's library is not privileged; they just get no buttons.

  import { onMount } from "svelte";
  import { transport } from "@/transport";
  import { auth } from "@/stores/auth.svelte";
  import type { Organization, OrgResult } from "./types";
  import { AppBadge, AppButton, AppDialog, AppInput, AppState } from "@/components/ui";
  import { t } from "@/i18n/index.svelte";

  let { onclose, onchanged }: { onclose?: () => void; onchanged?: () => void } = $props();

  type Status = "loading" | "ready" | "error";
  let status = $state<Status>("loading");
  let error = $state("");

  /** The organization on screen: the caller's own, or the one a super-admin picked from the list. */
  let current = $state<Organization | null>(null);
  /** Every organization, for the super-admin picker. Empty for everyone else. */
  let all = $state<Organization[]>([]);

  let addEmail = $state("");
  let addAsAdmin = $state(false);
  let busy = $state(false);

  // Creation form (super-admin only).
  let showCreate = $state(false);
  let newName = $state("");
  let newAdminEmail = $state("");

  const canManage = $derived(auth.state.isSuperAdmin || current?.myRole === "admin");

  const isSuperAdmin = $derived(auth.state.isSuperAdmin);

  async function load() {
    status = "loading";
    error = "";
    try {
      if (isSuperAdmin) {
        all = (await transport.invoke<Organization[]>("ListOrganizations")) ?? [];
      }
      // "Mine" is loaded for everyone including the platform owner: they may well belong to one, and
      // it is the sensible thing to open on.
      const mine = await transport.invoke<Organization | null>("GetMyOrganization");
      current = mine ?? null;
      status = "ready";
    } catch (e) {
      error = messageOf(e);
      status = "error";
    }
  }

  /**
   * Opens one organization from the super-admin list. The list response deliberately carries no
   * members — it is a list — so the roster is fetched for the one actually being looked at.
   */
  async function select(org: Organization) {
    if (current?.slug === org.slug) return;
    current = org;
    error = "";
    try {
      const full = await transport.invoke<Organization>("GetOrganization", { slug: org.slug });
      if (full) current = full;
    } catch (e) {
      error = messageOf(e);
    }
  }

  async function addMember() {
    const email = addEmail.trim();
    if (!email || !current) return;

    busy = true;
    error = "";
    try {
      const res = await transport.invoke<OrgResult>("AddOrganizationMember", {
        slug: current.slug,
        email,
        role: addAsAdmin ? "admin" : "member",
      });
      applyResult(res);
      if (res.ok) {
        addEmail = "";
        addAsAdmin = false;
      }
    } catch (e) {
      error = messageOf(e);
    } finally {
      busy = false;
    }
  }

  /**
   * Whether a roster row is the person reading it. Matched on email because that is the only
   * identity the auth state carries — it holds who you are, deliberately not an account id, since
   * nothing in the UI has needed one. The platform owner is never "self" for this purpose: their
   * powers do not come from the membership, so the controls stay available on their own row.
   */
  function isSelf(email: string | null | undefined): boolean {
    if (auth.state.isSuperAdmin) return false;
    const mine = auth.state.email;
    return !!mine && !!email && mine.toLowerCase() === email.toLowerCase();
  }

  async function setRole(userId: string, role: string) {
    if (!current) return;
    busy = true;
    error = "";
    try {
      applyResult(
        await transport.invoke<OrgResult>("SetOrganizationMemberRole", {
          slug: current.slug,
          userId,
          role,
        }),
      );
    } catch (e) {
      error = messageOf(e);
    } finally {
      busy = false;
    }
  }

  async function removeMember(userId: string, name: string) {
    if (!current) return;
    if (!window.confirm(t("org.confirmRemove", { name }))) return;

    busy = true;
    error = "";
    try {
      applyResult(
        await transport.invoke<OrgResult>("RemoveOrganizationMember", {
          slug: current.slug,
          userId,
        }),
      );
    } catch (e) {
      error = messageOf(e);
    } finally {
      busy = false;
    }
  }

  async function createOrganization() {
    const name = newName.trim();
    const adminEmail = newAdminEmail.trim();
    if (!name || !adminEmail) return;

    busy = true;
    error = "";
    try {
      const res = await transport.invoke<OrgResult>("CreateOrganization", { name, adminEmail });
      if (res.ok && res.organization) {
        all = [...all, res.organization].sort((a, b) => a.name.localeCompare(b.name));
        current = res.organization;
        newName = "";
        newAdminEmail = "";
        showCreate = false;
        onchanged?.();
      } else {
        error = res.error ?? t("org.actionFailed");
      }
    } catch (e) {
      error = messageOf(e);
    } finally {
      busy = false;
    }
  }

  /** Every write returns the organization's new state, so the roster never needs a second request. */
  function applyResult(res: OrgResult) {
    if (res.ok && res.organization) {
      current = res.organization;
      // Membership changes can move families in and out of view for the affected person; let the
      // caller refresh the grid rather than deciding that here.
      onchanged?.();
    } else {
      error = res.error ?? t("org.actionFailed");
    }
  }

  function messageOf(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
  }

  onMount(() => {
    void load();
  });
</script>

<AppDialog
  size="md"
  title={current?.name || t("org.title")}
  subtitle={current ? t("org.memberCount", { count: current.members.length || current.memberCount }) : ""}
  closeLabel={t("publish.close")}
  onclose={() => onclose?.()}
>
  {#if status === "loading"}
    <AppState kind="loading">{t("common.loading")}</AppState>
  {:else if status === "error"}
    <AppState kind="error" retryLabel={t("common.retry")} onretry={load}>
      {error || t("org.loadFailed")}
    </AppState>
  {:else}
    <!-- Platform owner: pick an organization, or create one. -->
    {#if isSuperAdmin}
      <div class="flex flex-col gap-2 border border-line rounded-surface p-3">
        <div class="flex items-center gap-2 flex-wrap">
          {#each all as o (o.slug)}
            <AppBadge
              selected={current?.slug === o.slug}
              title={o.name}
              onclick={() => select(o)}
              class="max-w-[12rem] truncate"
            >
              {o.name}
            </AppBadge>
          {/each}
          <button
            class="text-xs px-2.5 py-1 rounded-full border border-dashed border-line hover:border-accent hover:text-accent transition-colors"
            onclick={() => (showCreate = !showCreate)}
          >
            + {t("org.create")}
          </button>
        </div>

        {#if showCreate}
          <form
            class="flex flex-col gap-2 pt-1"
            onsubmit={(event) => {
              event.preventDefault();
              createOrganization();
            }}
          >
            <AppInput bind:value={newName} type="text" placeholder={t("org.name")} />
            <AppInput bind:value={newAdminEmail} type="email" placeholder={t("org.adminEmail")} />
            <AppButton
              type="submit"
              size="sm"
              disabled={busy || !newName.trim() || !newAdminEmail.trim()}
            >
              {busy ? t("common.loading") : t("org.create")}
            </AppButton>
          </form>
        {/if}
      </div>
    {/if}

    {#if !current}
      <p class="text-sm text-ink-muted py-8 text-center">{t("org.none")}</p>
    {:else}
      {#if error}
        <p class="text-xs text-error">{error}</p>
      {/if}

      <!-- Roster. -->
      <ul class="flex flex-col divide-y divide-line border border-line rounded-surface">
        {#each current.members as m (m.userId)}
          <li class="flex items-center gap-3 px-3 py-2">
            <div class="flex-1 min-w-0">
              <p class="text-sm truncate">{m.displayName || m.email}</p>
              <p class="text-xs text-ink-muted truncate">{m.email}</p>
            </div>

            <AppBadge tone={m.role === "admin" ? "accent" : "muted"} class="shrink-0">
              {m.role === "admin" ? t("org.admin") : t("org.member")}
            </AppBadge>

            {#if canManage}
              <!-- Stepping down stays available on your own row: handing the organisation over is a
                   real thing people do, it keeps your membership, and any admin can hand the role
                   back. Removing yourself does not — it clears your organisation outright, taking
                   this dialog with it and leaving nobody inside who can see you to invite you back.
                   The server refuses that either way; hiding the button is so the refusal is never a
                   surprise. The platform owner keeps both: their powers do not come from this
                   membership. -->
              <button
                class="text-xs text-ink-muted hover:text-accent transition-colors shrink-0 disabled:opacity-50"
                disabled={busy}
                onclick={() => setRole(m.userId, m.role === "admin" ? "member" : "admin")}
              >
                {m.role === "admin" ? t("org.makeMember") : t("org.makeAdmin")}
              </button>
              {#if isSelf(m.email)}
                <span class="text-xs text-ink-muted shrink-0">{t("org.you")}</span>
              {:else}
                <button
                  class="text-xs text-ink-muted hover:text-error transition-colors shrink-0 disabled:opacity-50"
                  disabled={busy}
                  onclick={() => removeMember(m.userId, m.displayName || m.email)}
                >
                  {t("org.remove")}
                </button>
              {/if}
            {/if}
          </li>
        {/each}
      </ul>

      <!-- Add a member. Registered accounts only, for now: there is no invitation mail yet,
           so an unknown email is refused rather than silently creating a pending nobody. -->
      {#if canManage}
        <form
          class="flex flex-col gap-2"
          onsubmit={(event) => {
            event.preventDefault();
            addMember();
          }}
        >
          <div class="flex items-center gap-2">
            <AppInput
              bind:value={addEmail}
              type="email"
              placeholder={t("org.emailHint")}
              class="flex-1"
            />
            <AppButton type="submit" size="sm" disabled={busy || !addEmail.trim()} class="shrink-0">
              {t("org.add")}
            </AppButton>
          </div>
          <label class="flex items-center gap-2 text-xs text-ink-muted">
            <input bind:checked={addAsAdmin} type="checkbox" class="accent-[var(--brand-accent)]" />
            {t("org.addAsAdmin")}
          </label>
        </form>
      {/if}
    {/if}
  {/if}
</AppDialog>
